import React, { useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import PageHeader from '../components/common/PageHeader';
import { ptaService, PtaAnalysisResult, PtaCompactResult, PtaChapter, PtaChapterResult, CostInfo } from '../services/ptaService';
import { convertToWav, isWavFile } from '../utils/audioConverter';

const CHAPTER_DEFINITIONS: { id: PtaChapter; labelEn: string; labelDe: string }[] = [
  { id: 'header', labelEn: 'Header Information', labelDe: 'Header-Informationen' },
  { id: 'ch2', labelEn: 'Ch. 2 – Escalation & Communication', labelDe: 'Kap. 2 – Eskalation & Kommunikation' },
  { id: 'ch3', labelEn: 'Ch. 3 – Engagement Details', labelDe: 'Kap. 3 – Engagement Details' },
  { id: 'ch4', labelEn: 'Ch. 4 – Test Environment', labelDe: 'Kap. 4 – Testumgebung' },
  { id: 'ch5', labelEn: 'Ch. 5 – Permission to Attack', labelDe: 'Kap. 5 – Permission to Attack' },
];

type AnalysisTextKey = 'general_info' | 'section_6_1' | 'section_6_2' | 'section_6_3';

const ANALYSIS_SECTIONS: Record<string, { key: AnalysisTextKey; title: string }[]> = {
  en: [
    { key: 'general_info', title: 'Extracted Application Information' },
    { key: 'section_6_1', title: '6.1 Application Description' },
    { key: 'section_6_2', title: '6.2 Use Cases' },
    { key: 'section_6_3', title: '6.3 Technologies, Frameworks and Programming Languages' },
  ],
  de: [
    { key: 'general_info', title: 'Extrahierte Applikationsinformationen' },
    { key: 'section_6_1', title: '6.1 Beschreibung der Applikation' },
    { key: 'section_6_2', title: '6.2 Beschreibung von Use Cases' },
    { key: 'section_6_3', title: '6.3 Verwendete Technologien, Frameworks und Programmiersprachen' },
  ],
};

const PTAPage: React.FC = () => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [language, setLanguage] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [convertedFile, setConvertedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [phase, setPhase] = useState<'idle' | 'converting' | 'uploading' | 'transcribing'>('idle');
  const [convertPhase, setConvertPhase] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [snackMsg, setSnackMsg] = useState<string | null>(null);

  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<PtaAnalysisResult | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [compactResult, setCompactResult] = useState<PtaCompactResult | null>(null);
  const [compacting, setCompacting] = useState(false);
  const [analysisView, setAnalysisView] = useState<'long' | 'short'>('long');

  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [transcribeCost, setTranscribeCost] = useState<CostInfo | null>(null);

  const [chapterResults, setChapterResults] = useState<Record<string, PtaChapterResult>>({});
  const [chapterLoading, setChapterLoading] = useState<Record<string, boolean>>({});
  const [chapterError, setChapterError] = useState<string | null>(null);

  const canTranscribe = language !== null && file !== null && !loading;
  const selectedSections = ANALYSIS_SECTIONS[language || 'en'] ?? ANALYSIS_SECTIONS.en;
  const analysisBothDone = !!analysisResult && !!compactResult;
  const analysisInProgress = analyzing || compacting;

  const combinedAnalysisText = (() => {
    if (!analysisBothDone) return '';
    if (analysisView === 'short') {
      return selectedSections
        .filter(({ key }) => key !== 'general_info')
        .map(({ key, title }) => `## ${title}\n\n${compactResult[key as keyof PtaCompactResult]}`.trim())
        .join('\n\n');
    }
    return selectedSections
      .map(({ key, title }) => `## ${title}\n\n${analysisResult[key]}`.trim())
      .join('\n\n');
  })();

  const combinedAnalysisCost = (analysisResult?.cost?.cost_eur ?? 0) + (compactResult?.cost?.cost_eur ?? 0);

  const formatCost = (eur: number) => `${eur.toFixed(4)} €`;

  const chapterTotalCost = Object.values(chapterResults).reduce(
    (sum, r) => sum + (r.cost?.cost_eur ?? 0), 0,
  );

  const anyChapterLoading = Object.values(chapterLoading).some(Boolean);
  const chaptersDone = CHAPTER_DEFINITIONS.filter((ch) => chapterResults[ch.id]);
  const chaptersWithInfo = chaptersDone.filter((ch) => chapterResults[ch.id]?.has_info);
  const combinedChapterText = chaptersWithInfo
    .map((ch) => {
      const r = chapterResults[ch.id];
      return `## ${r.title}\n\n${r.content}`;
    })
    .join('\n\n');

  const handleLanguageChange = (_: React.MouseEvent<HTMLElement>, newLang: string | null) => {
    if (newLang !== null) setLanguage(newLang);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    setFile(selected);
    setConvertedFile(null);
    setTranscript(null);
    setError(null);
    setAnalysisResult(null);
    setCompactResult(null);
    setAnalysisError(null);
    setAnalysisView('long');
    setTranscribeCost(null);
    setChapterResults({});
    setChapterError(null);
  };

  const handleTranscribe = async () => {
    if (!file || !language) return;
    setLoading(true);
    setTranscript(null);
    setError(null);
    setUploadPercent(0);
    setAnalysisResult(null);
    setCompactResult(null);
    setAnalysisError(null);
    setAnalysisView('long');
    setTranscribeCost(null);
    setChapterResults({});
    setChapterError(null);

    let wavFile = file;
    if (!isWavFile(file)) {
      try {
        setPhase('converting');
        wavFile = await convertToWav(file, setConvertPhase);
        setConvertedFile(wavFile);
      } catch (err: any) {
        console.error('Konvertierung fehlgeschlagen:', err);
        if (err?.message === 'TOO_LARGE') {
          setError(
            'Die Datei ist zu groß für die Browser-Konvertierung.\n' +
            'Bitte die Audiospur vorab lokal extrahieren:\n' +
            'ffmpeg -i video.mp4 -vn -ar 16000 -ac 1 output.wav',
          );
        } else {
          setError('Konvertierung fehlgeschlagen. Bitte die Browser-Konsole (F12) prüfen.');
        }
        setLoading(false);
        setPhase('idle');
        setConvertPhase(null);
        return;
      }
    }

    setConvertPhase(null);
    setPhase('uploading');
    try {
      const result = await ptaService.transcribe(wavFile, language, (percent) => {
        setUploadPercent(percent);
        if (percent >= 100) setPhase('transcribing');
      });
      setTranscript(result.transcript);
      setTranscribeCost(result.cost);
    } catch {
      setError('Transkription fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setLoading(false);
      setPhase('idle');
    }
  };

  const handleAnalyze = async () => {
    if (!transcript || !language) return;
    setAnalyzing(true);
    setAnalysisError(null);
    setAnalysisResult(null);
    setCompactResult(null);
    setAnalysisView('long');

    let longResult: PtaAnalysisResult | null = null;
    try {
      longResult = await ptaService.analyze(transcript, language);
      setAnalysisResult(longResult);
    } catch {
      setAnalysisError('Analyse fehlgeschlagen. Bitte erneut versuchen.');
      setAnalyzing(false);
      return;
    }
    setAnalyzing(false);

    setCompacting(true);
    try {
      const longSections = selectedSections
        .map(({ key, title }) => `## ${title}\n\n${longResult![key]}`)
        .join('\n\n');
      const compact = await ptaService.analyzeCompact(longSections, language);
      setCompactResult(compact);
    } catch {
      setAnalysisError('Kompaktversion fehlgeschlagen.');
    } finally {
      setCompacting(false);
    }
  };

  const handleChapterAnalyze = async () => {
    if (!transcript || !language) return;
    setChapterError(null);
    setChapterResults({});

    const chapters = CHAPTER_DEFINITIONS.map((ch) => ch.id);
    const initialLoading: Record<string, boolean> = {};
    chapters.forEach((id) => { initialLoading[id] = true; });
    setChapterLoading(initialLoading);

    const promises = chapters.map(async (chapterId) => {
      try {
        const result = await ptaService.analyzeChapter(transcript, language, chapterId);
        setChapterResults((prev) => ({ ...prev, [chapterId]: result }));
      } catch {
        setChapterResults((prev) => ({
          ...prev,
          [chapterId]: { chapter: chapterId, title: '', content: '', has_info: false, cost: { provider: 'openai', model: 'gpt-5.2', cost_eur: 0 } },
        }));
      } finally {
        setChapterLoading((prev) => ({ ...prev, [chapterId]: false }));
      }
    });

    try {
      await Promise.all(promises);
    } catch {
      setChapterError('Kapitel-Analyse teilweise fehlgeschlagen.');
    }
  };

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setSnackMsg('In Zwischenablage kopiert');
      setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      /* clipboard might be blocked */
    }
  };

  const markdownComponents = {
    p: ({ children, ...props }: any) => (
      <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.7, color: theme.palette.text.primary }} {...props}>
        {children}
      </Typography>
    ),
    li: ({ children, ...props }: any) => (
      <li style={{ marginBottom: 4, lineHeight: 1.7, color: theme.palette.text.primary }} {...props}>
        {children}
      </li>
    ),
    strong: ({ children, ...props }: any) => (
      <strong style={{ color: theme.palette.text.primary }} {...props}>{children}</strong>
    ),
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader
        title="PTA"
        subtitle="Pentest Agreement — Audio Transkription & Analyse"
        breadcrumbs={[
          { label: 'Workbench', path: '/workbench' },
          { label: 'PTA' },
        ]}
      />

      {/* Upload & transcribe controls */}
      <Paper
        sx={{
          p: 2,
          bgcolor: theme.palette.background.paper,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5, color: theme.palette.text.secondary }}>
              Sprache
            </Typography>
            <ToggleButtonGroup value={language} exclusive onChange={handleLanguageChange} size="small">
              <ToggleButton value="de" sx={{ px: 3 }}>Deutsch</ToggleButton>
              <ToggleButton value="en" sx={{ px: 3 }}>English</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ flex: 1, minWidth: 320 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, color: theme.palette.text.secondary }}>
              Audio- / Videodatei
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".wav,.mp4,.mp3,.m4a,.webm,.ogg,audio/*,video/*"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => fileInputRef.current?.click()}>
                Datei auswählen
              </Button>
              {file && (
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  {file.name} ({(file.size / 1024 / 1024).toFixed(1)} MB)
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {convertedFile && file && !isWavFile(file) && (
          <Typography variant="body2" sx={{ color: theme.palette.success.main, fontWeight: 500 }}>
            Lokal konvertiert: {(file.size / 1024 / 1024).toFixed(1)} MB → {(convertedFile.size / 1024 / 1024).toFixed(1)} MB WAV
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>
          MP4, MP3, M4A, WebM und andere Formate werden automatisch lokal zu WAV konvertiert.
        </Typography>

        {phase === 'converting' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {convertPhase || 'Konvertiere…'}
            </Typography>
          </Box>
        )}

        {error && (
          <Typography color="error" variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{error}</Typography>
        )}
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', minHeight: 'calc(100vh - 320px)' }}>
        <Paper
          sx={{
            flex: 1,
            p: 0,
            bgcolor: theme.palette.background.paper,
            borderRadius: 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 1.5,
              bgcolor: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.04)'
                : 'rgba(0,0,0,0.02)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DescriptionOutlinedIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Transkript
              </Typography>
              {transcribeCost && (
                <Tooltip title={`Deepgram nova-3 · ${Math.round((transcribeCost.duration_seconds ?? 0) / 60)} Min Audio`}>
                  <Typography variant="caption" sx={{ color: theme.palette.success.main, fontWeight: 600, ml: 0.5 }}>
                    {formatCost(transcribeCost.cost_eur)}
                  </Typography>
                </Tooltip>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title={copiedKey === 'transcript' ? 'Kopiert!' : 'Transkript kopieren'}>
                <span>
                  <IconButton
                    onClick={() => transcript && copyToClipboard(transcript, 'transcript')}
                    size="small"
                    sx={{ color: theme.palette.text.secondary }}
                    disabled={!transcript}
                  >
                    {copiedKey === 'transcript' ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                size="small"
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <MicIcon />}
                disabled={!canTranscribe}
                onClick={handleTranscribe}
                sx={{ fontWeight: 700 }}
              >
                {phase === 'converting' && (convertPhase || 'Konvertiere...')}
                {phase === 'uploading' && `Upload ${uploadPercent}%`}
                {phase === 'transcribing' && 'Transkribiere...'}
                {phase === 'idle' && 'Transcribe'}
              </Button>
            </Box>
          </Box>
          <Divider />
          <Box
            sx={{
              px: 3,
              py: 2,
              flex: 1,
              overflowY: 'auto',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                color: theme.palette.text.primary,
                lineHeight: 1.8,
                fontFamily: 'inherit',
              }}
            >
              {transcript || 'Noch kein Transkript vorhanden'}
            </Typography>
          </Box>
        </Paper>
        <Paper
          sx={{
            flex: 1,
            p: 0,
            bgcolor: theme.palette.background.paper,
            borderRadius: 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 1.5,
              bgcolor: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.04)'
                : 'rgba(0,0,0,0.02)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Analyse
              </Typography>
              {analysisBothDone && (
                <ToggleButtonGroup
                  value={analysisView}
                  exclusive
                  onChange={(_, v) => { if (v) setAnalysisView(v); }}
                  size="small"
                  sx={{ ml: 0.5 }}
                >
                  <ToggleButton value="long" sx={{ px: 1.5, py: 0, fontSize: '0.7rem', textTransform: 'none' }}>Lang</ToggleButton>
                  <ToggleButton value="short" sx={{ px: 1.5, py: 0, fontSize: '0.7rem', textTransform: 'none' }}>Kurz</ToggleButton>
                </ToggleButtonGroup>
              )}
              {combinedAnalysisCost > 0 && !analysisInProgress && (
                <Tooltip title="Kosten Lang + Kurz kombiniert">
                  <Typography variant="caption" sx={{ color: theme.palette.success.main, fontWeight: 600 }}>
                    {formatCost(combinedAnalysisCost)}
                  </Typography>
                </Tooltip>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {analysisInProgress && (
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  {compacting ? 'Kompaktiere...' : '30–60 Sek.'}
                </Typography>
              )}
              <Tooltip title={copiedKey === 'analysis_all' ? 'Kopiert!' : 'Alles kopieren'}>
                <span>
                  <IconButton
                    onClick={() => combinedAnalysisText && copyToClipboard(combinedAnalysisText, 'analysis_all')}
                    size="small"
                    sx={{ color: theme.palette.text.secondary }}
                    disabled={!combinedAnalysisText}
                  >
                    {copiedKey === 'analysis_all' ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                size="small"
                startIcon={analysisInProgress ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                disabled={!transcript || analysisInProgress}
                onClick={handleAnalyze}
                sx={{ fontWeight: 700 }}
              >
                {analyzing ? 'Analysiere...' : compacting ? 'Kompaktiere...' : 'Analyse starten'}
              </Button>
            </Box>
          </Box>
          <Divider />
          {analysisInProgress && <LinearProgress />}
          <Box sx={{ px: 3, py: 2, flex: 1, overflowY: 'auto' }}>
            {analysisError && (
              <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                {analysisError}
              </Typography>
            )}
            {analysisInProgress && !analysisBothDone && (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {analyzing ? 'Erstelle ausführliche Analyse...' : 'Erstelle kompakte Version...'}
              </Typography>
            )}
            {!analysisInProgress && !analysisBothDone && !analysisError && (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                Noch keine Analyse vorhanden
              </Typography>
            )}
            {analysisBothDone && (
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {combinedAnalysisText}
              </ReactMarkdown>
            )}
          </Box>
        </Paper>

        <Paper
          sx={{
            flex: 1,
            p: 0,
            bgcolor: theme.palette.background.paper,
            borderRadius: 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 1.5,
              bgcolor: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.04)'
                : 'rgba(0,0,0,0.02)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Kapitel-Extraktion
              </Typography>
              {chapterTotalCost > 0 && !anyChapterLoading && (
                <Tooltip title={`gpt-5.2 · ${CHAPTER_DEFINITIONS.length} Kapitel`}>
                  <Typography variant="caption" sx={{ color: theme.palette.success.main, fontWeight: 600 }}>
                    {formatCost(chapterTotalCost)}
                  </Typography>
                </Tooltip>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {anyChapterLoading && (
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  {Object.values(chapterLoading).filter(Boolean).length} / {CHAPTER_DEFINITIONS.length}
                </Typography>
              )}
              <Tooltip title={copiedKey === 'chapter_all' ? 'Kopiert!' : 'Alle Kapitel kopieren'}>
                <span>
                  <IconButton
                    onClick={() => combinedChapterText && copyToClipboard(combinedChapterText, 'chapter_all')}
                    size="small"
                    sx={{ color: theme.palette.text.secondary }}
                    disabled={!combinedChapterText}
                  >
                    {copiedKey === 'chapter_all' ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                size="small"
                startIcon={anyChapterLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                disabled={!transcript || anyChapterLoading}
                onClick={handleChapterAnalyze}
                sx={{ fontWeight: 700 }}
              >
                {anyChapterLoading ? 'Extrahiere...' : 'Kapitel extrahieren'}
              </Button>
            </Box>
          </Box>
          <Divider />
          <Box sx={{ flex: 1, overflowY: 'auto', px: 1, py: 1 }}>
            {chapterError && (
              <Typography color="error" variant="body2" sx={{ px: 2, py: 1 }}>
                {chapterError}
              </Typography>
            )}

            {CHAPTER_DEFINITIONS.length > 0 && !anyChapterLoading && chaptersDone.length === 0 && (
              <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Extrahiert Informationen pro PTA-Kapitel aus dem Transkript.
                </Typography>
              </Box>
            )}

            {CHAPTER_DEFINITIONS.map((ch) => {
              const isLoading = chapterLoading[ch.id];
              const result = chapterResults[ch.id];
              const label = language === 'de' ? ch.labelDe : ch.labelEn;

              const noContent = !isLoading && result && !result.has_info;

              return (
                <Accordion
                  key={ch.id}
                  disabled={noContent || (!result && !isLoading)}
                  disableGutters
                  sx={{
                    '&:before': { display: 'none' },
                    boxShadow: 'none',
                    bgcolor: 'transparent',
                    opacity: noContent ? 0.5 : 1,
                  }}
                >
                  <AccordionSummary
                    expandIcon={result?.has_info ? <ExpandMoreIcon /> : undefined}
                    sx={{
                      minHeight: 44,
                      '& .MuiAccordionSummary-content': { my: 0.5, alignItems: 'center', gap: 1 },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                      {label}
                    </Typography>
                    {isLoading && <CircularProgress size={16} />}
                    {noContent && (
                      <Tooltip title="Keine relevanten Informationen im Transkript gefunden">
                        <InfoOutlinedIcon fontSize="small" sx={{ color: theme.palette.text.disabled }} />
                      </Tooltip>
                    )}
                    {result?.has_info && (
                      <Tooltip title={copiedKey === `ch_${ch.id}` ? 'Kopiert!' : 'Kapitel kopieren'}>
                        <IconButton
                          size="small"
                          sx={{ color: theme.palette.text.secondary }}
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(result.content, `ch_${ch.id}`);
                          }}
                        >
                          {copiedKey === `ch_${ch.id}` ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </AccordionSummary>
                  {isLoading && <LinearProgress sx={{ mx: 2 }} />}
                  {result?.has_info && (
                    <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                        {result.content}
                      </ReactMarkdown>
                    </AccordionDetails>
                  )}
                </Accordion>
              );
            })}
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={snackMsg !== null}
        autoHideDuration={2000}
        onClose={() => setSnackMsg(null)}
        message={snackMsg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default PTAPage;
